// The Internet OS tree — one OsNode model the whole shell renders, derived
// from the existing data (projects · dimensions · nodes · artifacts · tasks).
// These builders are PURE (they take already-fetched plain data), so they run
// the same on the server (initial SSR + the /api/os/project route) and would
// run on the client too. No Supabase import here.

import { fmtClock } from "../format/time";
import { fallbackSummary } from "../views";
import { parseSkillDoc } from "../skills/manifest";
import { symbolFor } from "./symbols";

export type OsKind =
  | "root"
  | "section"
  | "project"
  | "dimension"
  | "group"
  | "node"
  | "artifact"
  | "task"
  | "panel";

export type PanelKind = "you" | "netti" | "ours" | "activity" | "tasks" | "parked" | "connections" | "agents" | "settings" | "skills";

export interface OsStat {
  label: string;
  value: string | number;
}

export interface OsCommit {
  hash: string;
  summary: string;
  /** the Narrator Agent's authored, continuity-aware line — what changed & why,
   *  in innernet's voice. Null until narrated; the timeline falls back to summary. */
  description?: string | null;
  trigger: string | null;
  committedAt: string;
  /** the memory chain's parent (not a git parent) — null at the root. */
  parentHash?: string | null;
  /** the capture's tags (diff->tags): source:git-commit, sha:, branch:, decision… */
  tags?: string[];
}

export interface OsNode {
  key: string; // unique among siblings; also the layout child-key
  path: string; // full path string, e.g. /projects/<id>/clients
  kind: OsKind;
  name: string;
  symbol: string;
  icon: "folder" | "page";
  summary: string;
  stats: OsStat[];
  lastActiveAt?: string | null;
  createdAt?: string | null; // when the entity was first minted (artifact meta line)
  children?: OsNode[]; // folders
  document?: string; // pages (markdown)
  panel?: PanelKind; // reuse an existing dashboard panel
  hidden?: boolean; // kept out of the sibling nav strip (reachable only by path)
  /** a child that stands off its parent's word strip while staying its child (an agent's sub-agents: the strip
   *  under an agent is its spaces, and the sub-agents are reached through its sub-agents space). Unlike `hidden`,
   *  standing on one still shows its own siblings. */
  offStrip?: boolean;
  /** an agent's projects space (147): the agent it belongs to and the projects tied to it, so the place can be
   *  drawn with those projects inside it, the SAME project nodes the tree holds, re-pathed under the agent */
  agentProjects?: { agentName: string; ties: AgentProjectTie[] };
  hintKey?: string; // Noticer registry key for this sibling (richer than `summary`)
  projectId?: string; // owning project (for download/share scoping)
  lazy?: boolean; // children fetched on first entry (project folders at root)
  commits?: OsCommit[]; // a project's commit history (drives the timeline view)
  color?: string | null; // a per-project folder colour override (tints the face)
  iconKey?: string | null; // the raw per-project glyph override (null = auto-picked)
  parked?: boolean; // rests in /parked (project or idea) — menus offer "unpark"
  editKey?: string; // raw DB identifier for an editable doc (a dimension's real name)
  /** EVERY DOCUMENT IS AN ARTIFACT. A dimension, a memory node and a living
   *  document are the same species — a document innernet holds — and each one
   *  keeps its own single source of truth (its own row, reached by the `dim:` /
   *  `node:` / `art:` key prefix). What used to make an "artifact" a separate
   *  class is not the document, it is a BEHAVIOUR: whether innernet keeps
   *  re-organizing it as fragments stream in. That behaviour is this flag.
   *
   *  So `live` is the thing that differs, not the kind. Today only documents
   *  backed by the artifacts table run live; the flag is what lets any document
   *  be switched on later without becoming a different sort of thing. */
  live?: boolean;
  /** a node that lives at its OWN route (a shared space at /ours/<slug>) rather
   *  than inside the shell's path tree. Menus route here instead of calling goTo,
   *  so the ≡ can drill into places the shell itself doesn't render. */
  href?: string;
  /** WHOSE this is, when that isn't obvious — set only in a shared room, where
   *  four people can each stand a folder called "innernet" side by side. The
   *  folder then wears their mark and their name beneath it, and the emoji it
   *  carries is the one THEY set on it. In your own innernet everything is
   *  yours, so this stays undefined and nothing is drawn. */
  owner?: {
    /** what the byline READS — "sam", or "you" for your own. */
    name: string | null;
    avatarUrl: string | null;
    /** the name the MARK takes its letter from, when it differs from the byline
     *  (your own folder reads "you" but must still wear your own initial). */
    avatarName?: string | null;
  } | null;
  /** the caller may change this document (a room folder linked in at 'edit'). */
  canEdit?: boolean;
  /** a project's declared type (projects.type: code · product · space …). */
  projectType?: string;
  /** the project carries a tagline someone wrote (not the generated fallback), so
   *  its description is worth a line under the head (law_say_it_once). */
  hasTagline?: boolean;
  /** this project IS a codebase (lib/os/codeProject.ts — the one rule): it wears
   *  the repo mark beside its name and opens a `repo` view. */
  code?: boolean;
  /** how many of its direct children are code projects — a parent shows the
   *  `repo` chip (a grid of them) before its subtree has loaded. */
  codeChildren?: number;
}

// ---- helpers ----------------------------------------------------------------

// Natural, compact history: "just now" → minutes → hours → days, then an ACTUAL
// DATE once it's old enough that a relative count stops being legible — so you
// always know WHEN something happened. Used throughout (stat lines, activity).
export function relTime(iso?: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso);
  const s = Math.floor((Date.now() - then.getTime()) / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d`;
  const sameYear = then.getFullYear() === new Date().getFullYear();
  return then.toLocaleDateString("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

// A plain calendar date (e.g. "2026-05-27") for the artifact "created …" meta
// line — the mono, factual stamp the Figma shows at the top of every document.
export function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
}

export const maxDate = (...iso: (string | null | undefined)[]): string | undefined => {
  const ts = iso.filter(Boolean).map((s) => new Date(s as string).getTime());
  return ts.length ? new Date(Math.max(...ts)).toISOString() : undefined;
};

/** Path segment for a name — the ONE slugifier every tree path uses. Exported
 *  so deep links written elsewhere (activity rows, netti's fold) land on the
 *  exact same path the tree builds. */
export const seg = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const firstLine = (md: string): string => {
  const line = (md || "")
    .split("\n")
    .map((l) => l.replace(/^#+\s*/, "").replace(/[*_`>#-]/g, "").trim())
    .find((l) => l.length > 0);
  return line ? line.slice(0, 160) : "";
};
export const plural = (t: string) => (/s$/.test(t) ? t : `${t}s`);

/** WHERE ONE MEMORY LIVES in the dashboard — built by the very rules the tree
 *  below lays pages and cells out by, so a link to "this page" (the forget
 *  preview hands one to the person before anything is deleted) lands exactly
 *  on it. A skill gathers into the project's skills folder; a cell sits in the
 *  folder named for its type. If a path ever drifts, the shell walks up to the
 *  nearest ancestor that exists, so a link still lands inside the project. */
export function memoryPath(
  projectId: string,
  m: { kind: "page"; name: string; content: string } | { kind: "cell"; type: string; title: string },
): string {
  const base = `/projects/${projectId}`;
  if (m.kind === "page") {
    return parseSkillDoc(m.content, seg(m.name)) ? `${base}/skills/${seg(m.name)}` : `${base}/${seg(m.name)}`;
  }
  return `${base}/${seg(plural(m.type))}/${seg(m.title)}`;
}

/** True when every child is a document (no sub-folders) — render as a carousel. */
export function allPages(node: OsNode): boolean {
  return !!node.children?.length && node.children.every((c) => c.icon === "page");
}

/** The level's default view chip — the orbit, at every level. The deep orbit
 *  field scatters the whole rooted subtree (folders AND pages), so it stands
 *  as the default even where the level itself holds only documents. */
export function defaultView(_node: OsNode): string {
  // GRID, everywhere (John, 2026-09-02): a level opens as the grid of what is
  // in it; the orbit stays one chip away.
  return "grid";
}

/** Walk the tree by a path string ('/projects/<id>/clients'). */
export function findByPath(root: OsNode, path: string): OsNode | null {
  const want = path.replace(/\/+$/, "") || "/";
  const visit = (n: OsNode, at: string): OsNode | null => {
    if (n.path === at) return n;
    for (const c of n.children ?? []) {
      const hit = visit(c, at);
      if (hit) return hit;
    }
    return null;
  };
  const hit = visit(root, want);
  if (hit) return hit.agentProjects ? projectsSpace(root, hit) : hit;
  // a project opened INSIDE an agent (147): the same project node the tree holds at /projects/<id>, re-pathed
  const inside = AGENT_PROJECT_PATH.exec(want);
  if (inside) {
    const space = visit(root, inside[1]);
    const tie = space?.agentProjects?.ties.find((t) => t.id === inside[2]);
    if (!space?.agentProjects || !tie) return null; // only a project tied to the agent opens inside it
    const canonical = visit(root, `/projects/${inside[2]}`);
    if (!canonical) return null;
    const alias = rerooted(canonical, `${inside[1]}/${inside[2]}`, space.agentProjects.agentName, tie);
    return inside[3] ? visit(alias, want) : alias;
  }
  // one of a product agent's PEOPLE: a place the tree never lists one by one (an agent can keep something about a
  // hundred thousand of them), so it is worked out from the people place it stands under
  const who = PERSON_PATH.exec(want);
  const people = who ? visit(root, `${who[1]}/people`) : null;
  return people && who && people.key.startsWith("agent-people:") ? agentPersonNode(people, who[2]) : null;
}

// ── projects inside an agent (147) ───────────────────────────────────────────
// A project tied to an agent opens at /agents/<agent>/projects/<id>/…, inside the agent, and it is the SAME project:
// the node the tree holds at /projects/<id>, with every path moved under the agent. Keys, the project id, the
// documents and their edit keys stay the same, so an edit made here is an edit to the one project. Nothing is copied
// into the tree: the moved nodes are worked out on demand and remembered against the node they came from, so a
// render always sees the same object and a fresh splice of the project leaves them behind with it.

/** /agents/<agent>[/<sub>]/projects/<id>[/…]: the space, the project id, and the rest */
const AGENT_PROJECT_PATH = /^(\/agents\/.+?\/projects)\/([^/]+)(\/.*)?$/;

/** The project a path stands in, and its own path: /projects/<id>/… as it is, or the same project opened inside an
 *  agent. The shell loads the project by this, whichever door it was opened through. */
export function canonicalProjectOf(path: string): { pid: string; canonical: string; depth: number } | null {
  const direct = /^\/projects\/([^/]+)(\/.*)?$/.exec(path);
  if (direct) return { pid: direct[1], canonical: path, depth: direct[2] ? direct[2].split("/").filter(Boolean).length : 0 };
  const inside = AGENT_PROJECT_PATH.exec(path);
  if (!inside) return null;
  const rest = inside[3] ?? "";
  return { pid: inside[2], canonical: `/projects/${inside[2]}${rest}`, depth: rest.split("/").filter(Boolean).length };
}

const rerootMemo = new WeakMap<OsNode, Map<string, OsNode>>();

/** the project node, every path moved from /projects/<id> to `base`, its stats line saying whose it is */
function rerooted(canonical: OsNode, base: string, agentName: string, tie: AgentProjectTie): OsNode {
  let byBase = rerootMemo.get(canonical);
  if (!byBase) rerootMemo.set(canonical, (byBase = new Map()));
  const memoKey = `${base}\u001f${agentName}\u001f${tie.home ? 1 : 0}`;
  const had = byBase.get(memoKey);
  if (had) return had;
  const from = canonical.path;
  const move = (n: OsNode): OsNode => ({
    ...n,
    path: n.path === from ? base : n.path.startsWith(`${from}/`) ? `${base}${n.path.slice(from.length)}` : n.path,
    ...(n.children ? { children: n.children.map(move) } : {}),
  });
  const moved = move(canonical);
  // law: a person's own project inside an agent says so, in the one line the navigator gives a place
  const node: OsNode = {
    ...moved,
    stats: tie.home ? [{ value: "lives with", label: agentName }] : [{ value: "yours", label: `· linked to ${agentName}` }],
  };
  byBase.set(memoKey, node);
  return node;
}

const spaceMemo = new WeakMap<OsNode, { root: OsNode; node: OsNode }>();

/** the projects space with its projects inside it: each tied project the tree holds, re-pathed under the space */
function projectsSpace(root: OsNode, space: OsNode): OsNode {
  const had = spaceMemo.get(space);
  // the projects inside are the tree's own: a new tree (a project spliced in, renamed) is a new answer
  if (had && had.root === root) return had.node;
  const ties = space.agentProjects?.ties ?? [];
  const kids: OsNode[] = [];
  for (const t of ties) {
    const canonical = findByPath(root, `/projects/${t.id}`);
    if (canonical && !canonical.agentProjects) kids.push(rerooted(canonical, `${space.path}/${t.id}`, space.agentProjects?.agentName ?? "", t));
  }
  const node: OsNode = { ...space, children: kids };
  spaceMemo.set(space, { root, node });
  return node;
}

/** Where an agent lands: its overview. An agent's own path is a door, never a place to stand (its strip would be the
 *  other agents, not its spaces). Anything else is returned as it is. */
export function agentLanding(node: OsNode | null): OsNode | null {
  if (!node || !node.key.startsWith("agent:")) return node;
  return node.children?.find((c) => c.key.startsWith("agent-overview:")) ?? node;
}

/** The parent path of a node path. Returns null for the root ('/'). */
export function parentPath(path: string): string | null {
  if (path === "/" || path === "") return null;
  const parts = path.split("/").filter(Boolean);
  parts.pop();
  return parts.length ? "/" + parts.join("/") : "/";
}

/** Immutably replace the node at subtree.path within the tree (used to splice a
 *  lazily-fetched project subtree into the root). */
export function spliceSubtree(root: OsNode, subtree: OsNode): OsNode {
  if (root.path === subtree.path) return subtree;
  if (!root.children) return root;
  return { ...root, children: root.children.map((c) => spliceSubtree(c, subtree)) };
}

// ---- root (the dashboard home) ---------------------------------------------

export interface RootProject {
  id: string;
  slug: string;
  name: string;
  type: string;
  tagline: string | null;
  dimCount: number;
  nodeCount: number;
  updated_at: string;
  parentId?: string | null; // the project this nests inside (null = top level)
  color?: string | null; // per-project folder colour override (null = organism hue)
  icon?: string | null; // per-project glyph override (null = auto-picked)
  parked?: boolean; // archived — rides the same array; buildRoot routes it to /parked
  code?: boolean; // a codebase (lib/os/codeProject.ts) — the repo mark + the repo view
  codeChildren?: number; // code projects nested directly under this one
}

// PROJECTS LEADS. There used to be a `home` desktop in front of it — a second
// arrangement of the same folders, holding nothing projects didn't already hold
// — so arriving meant landing on an empty room and walking one step further to
// the work. It is gone; projects is where you arrive. (`/home` still resolves,
// straight through to /projects, so old links and deep links keep working.)
//
// The root sibling nav is: projects · you · netti · skills · connections · agents …
// Everything else (parked · dev) stays reachable by path — opened from the
// identity menu or a deep link — but never sits in the navigator strip.
// `hidden: true` keeps a panel out of the sibling rail.
const PANELS: { key: PanelKind; summary: string; hidden?: boolean }[] = [
  { key: "you", summary: "your days, your story, and everything innernet holds about you." },
  { key: "netti", summary: "netti — the living body of your memory. observe it, ask it, and shape the ground it lives on." },
  { key: "skills", summary: "what you can teach an AI — every page you keep that carries a skill, each one a link you can hand out." },
  { key: "connections", summary: "the AI tools reading and growing this same memory, live." },
  // beta (F.agents): the row exists for everyone, but buildRoot seats it only when
  // `opts.agents` says this person may see it, the way tasks and ours are seated.
  { key: "agents", summary: "the agents you keep, and what they learn about you." },
  { key: "ours", summary: "the shared side of your innernet — rooms of people standing around one memory. teams, companies, families." },
  { key: "activity", summary: "a narrated log of everything touched — what your AIs did, and the moves innernet makes on its own." },
  { key: "tasks", summary: "every open loop across all your projects — one running checklist, sortable by project." },
  { key: "parked", summary: "ideas set aside on a branch — out of the way, never lost.", hidden: true },
  // settings rides in the nav strip again, right after activity (still reachable
  // from the avatar too).
  { key: "settings", summary: "your account, security, and the data innernet keeps for you." },
];

// One project → its root folder node. Factored out so the SAME shape is built
// at SSR (buildRoot) and when a project arrives LIVE (the dashboard subscription
// folds a freshly-pushed/created project into the tree). lazy:true → its inner
// content loads on first entry.
export function projectFolderNode(p: RootProject): OsNode {
  return {
    key: p.id,
    path: `/projects/${p.id}`,
    kind: "project",
    name: p.name,
    symbol: symbolFor({ kind: "project", name: p.name, type: p.type }, p.icon),
    icon: "folder",
    color: p.color ?? null,
    iconKey: p.icon ?? null,
    summary: p.tagline?.trim() || fallbackSummary(p.name, p.type, p.dimCount, p.nodeCount),
    // cells = the project's whole memory size (dimensions + nodes) — the single
    // figure we surface as a project's status everywhere (was domains + nodes).
    stats: [{ label: "cells", value: p.dimCount + p.nodeCount }],
    lastActiveAt: p.updated_at,
    projectId: p.id,
    lazy: true,
    projectType: p.type,
    ...(p.tagline?.trim() ? { hasTagline: true } : {}),
    ...(p.code ? { code: true } : {}),
    ...(p.codeChildren ? { codeChildren: p.codeChildren } : {}),
    ...(p.parked ? { parked: true } : {}),
  };
}

// ---- the park ----------------------------------------------------------------

/** A parked branch idea — the minimal structural shape (the dashboard's
 *  DashParked satisfies it), rendered as a document page inside /parked. */
export interface ParkedIdeaLike {
  id: string;
  name: string;
  summary: string | null;
  created_at: string;
  project_name?: string | null;
}

/** A parked project — RootProject-shaped with parked:true (GET /api/os/parked's
 *  ParkedProject satisfies it). */
export type ParkedProjectLike = RootProject;

/** The /parked node's children — parked project folders first (openable, marked
 *  parked so menus can offer "unpark"), then each parked idea as a document page:
 *  a files-only park IS the artifacts view. Shared by the initial buildRoot and
 *  the shell's live park/unpark refresh (which refetches GET /api/os/parked and
 *  splices exactly this shape back in). Idea pages carry NO projectId — they're
 *  map branches, not project rows, so rename/move affordances stay off. */
export function buildParkedChildren(parkedProjects: ParkedProjectLike[], ideas: ParkedIdeaLike[]): OsNode[] {
  const folders = parkedProjects.map((p) => projectFolderNode({ ...p, parked: true }));
  const seen = new Set<string>();
  const pages: OsNode[] = ideas.map((b) => {
    const slug = seg(b.name) || b.id;
    const path = seen.has(`/parked/${slug}`) ? `/parked/${b.id}` : `/parked/${slug}`;
    seen.add(path);
    return {
      key: `parked:${b.id}`,
      path,
      kind: "artifact" as const,
      name: b.name,
      symbol: symbolFor({ kind: "artifact", name: b.name }),
      icon: "page" as const,
      summary: b.summary?.trim() || "an idea resting on a branch.",
      stats: [{ label: "resting", value: "" }],
      lastActiveAt: b.created_at,
      createdAt: b.created_at,
      document: `# ${b.name}\n\n*resting${b.project_name ? ` in ${b.project_name}` : ""} · since ${fmtDate(b.created_at)}*\n\n${b.summary ?? ""}`.trimEnd(),
      parked: true,
    };
  });
  return [...folders, ...pages];
}

/** Per-panel status line (the navigator's "N items ● lastActive" chip). Lets a
 *  data-bearing panel (you · activity · parked · connections · agents) show the SAME status
 *  chip in the SAME navigator location as a project section — instead of a blank
 *  stat line. Threaded in from the dashboard, where the counts/sync time live.
 *  `summary` optionally OVERRIDES the panel's static description with a live one.
 *  `parkedIdeas` is read on the `parked` key only — the branch ideas that become
 *  document pages inside /parked. They ride this already-threaded channel
 *  (dashboard → shell → buildRoot) so the tree can seed the park's pages. */
export type PanelMeta = Partial<
  Record<PanelKind, { stats?: OsStat[]; lastActiveAt?: string | null; summary?: string; parkedIdeas?: ParkedIdeaLike[] }>
>;

/** A ROOM, as the tree sees it. Rooms live at their OWN route (/ours/<slug>)
 *  but hang under the `ours` panel so the ≡ menu can drill into them —
 *  innernet › yours › ours › <room> — like any other directory. (The code
 *  keeps the schema's word, `space`; the language the user reads is "room".) */
export interface RootSpace {
  id: string;
  slug: string;
  name: string;
  tagline?: string | null;
  memberCount?: number;
  linkCount?: number;
  createdAt?: string | null;
}

export function spaceFolderNode(s: RootSpace): OsNode {
  const people = s.memberCount ?? 0;
  return {
    key: `space:${s.slug}`,
    path: `/ours/${s.slug}`,
    href: `/ours/${s.slug}`,
    kind: "project",
    name: s.name,
    symbol: symbolFor({ kind: "project", name: s.name, type: "space" }),
    icon: "folder",
    summary: s.tagline?.trim() || `a room ${people === 1 ? "one person stands" : `${people} people stand`} in.`,
    stats: [{ label: people === 1 ? "person" : "people", value: people }],
    lastActiveAt: s.createdAt ?? null,
  };
}

/** An AGENT, as the tree sees it. Every agent a person keeps hangs under the
 *  `agents` panel as a real path (/agents/<slug>, and /agents/<parent>/<slug> for
 *  a sub-agent), so the shell can stand INSIDE one: the crumbs name where you
 *  are, the navigator's title names it, a reload or a link lands on it. Every one
 *  of them still renders the agents panel (kind "panel"): the panel reads the
 *  path (OsShellApi.path) and draws the home, or that agent's own page.
 *
 *  The fields beyond the identity are optional on purpose: the server seed
 *  (app/dashboard/data.ts) knows who the agents are before it knows what each one
 *  keeps, and a node with no figure shows no figure rather than a zero. */
export interface RootAgent {
  id: string;
  slug: string;
  name: string;
  purpose?: string | null;
  status: "active" | "paused";
  /** what it keeps (null/undefined = not read yet) */
  memories?: number | null;
  /** asks about you it is holding for your yes */
  held?: number | null;
  lastActiveAt?: string | null;
  /** the SLUG of the agent it works under (null/undefined = a top-level agent) */
  parentSlug?: string | null;
  /** works for you (personal) or for other people (product). A product agent has its people place from the first paint,
   *  before anyone is in it (null/undefined = not read yet) */
  kind?: "personal" | "product" | null;
  /** how many people it keeps something about (null/undefined = not read yet). Any agent that has some gets the place */
  users?: number | null;
  /** the projects tied to it (147), by id: they open inside its projects space (null/undefined = not read yet) */
  projects?: AgentProjectTie[] | null;
}

/** one project tied to an agent, as the tree needs it: which, and whose it is */
export interface AgentProjectTie {
  id: string;
  /** the project lives with the agent (made or moved there); false = it is the person's own, linked */
  home: boolean;
  folder: boolean;
}

/** where an agent lives: its slug as a path segment, so /agents/grok-bot and the
 *  API's `grok-bot` name the same agent. */
const agentSeg = (a: Pick<RootAgent, "slug" | "id">) => seg(a.slug) || a.id;

const groupNum = (n: number) => n.toLocaleString("en-US");

/** THE SPACES INSIDE AN AGENT, as real tree paths, so the shell's navigator draws them as the big words under the
 *  agent (the crumb reads innernet / agents / <agent>, the strip reads overview · memory · projects …) and its crumbs,
 *  menu and back button work in each:
 *    /agents/<slug>/overview                    its head and what its memory did (where the agent itself lands)
 *    /agents/<slug>/memory                      what it keeps
 *    /agents/<slug>/projects                    the projects tied to it, each opening inside it (/projects/<id>/…)
 *    /agents/<slug>/with-you                    what it holds of you
 *    /agents/<slug>/activity                    what it did
 *    /agents/<slug>/rituals                     what it runs, where, and when (AGENT-WORKFLOWS-PLAN.md)
 *    /agents/<slug>/sub-agents                  the agents that work under it (a top-level agent only)
 *    /agents/<slug>/people(/<user id>)          who it remembers (a product agent, or any agent that has some)
 *    /agents/<slug>/settings                    its picture, how it remembers, its keys, its webhook, and removing it
 *  and the same under a sub-agent (/agents/<parent>/<sub>/memory …). They come before the sub-agents among the agent's
 *  children, and the sub-agents stand off the strip. None of these words can be an agent's own slug (lib/agents/policy),
 *  so a path under an agent is never a sub-agent by accident. */
export const AGENT_PLACES = ["overview", "memory", "projects", "with-you", "activity", "rituals", "sub-agents", "people", "settings"] as const;
export type AgentPlace = (typeof AGENT_PLACES)[number];

/** the people place stands for a product agent, and for any agent that keeps something about someone */
export const hasPeople = (a: Pick<RootAgent, "kind" | "users">) => a.kind === "product" || (a.users ?? 0) > 0;

/** a person's id (the one the agent was given) as one path segment */
export const personSeg = (userId: string) => encodeURIComponent(userId);

/** the id a path segment stands for (a malformed escape is the segment as it is) */
export function personIdOf(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** where one of an agent's people lives: its people place, then them */
export const agentPersonPath = (agentPath: string, userId: string) => `${agentPath.replace(/\/+$/, "")}/people/${personSeg(userId)}`;

/** /agents/<slug>[/<sub>]/people/<segment>, the agent's own path and the person's segment */
const PERSON_PATH = /^(\/agents(?:\/[^/]+){1,2})\/people\/([^/]+)$/;

// a person is worked out, not stored, so the one a render asks for must be the same object every time (the shell keys effects
// on the current node): remembered against the people node it came from, which changes when the tree is rebuilt
const personNodes = new WeakMap<OsNode, Map<string, OsNode>>();

function agentPersonNode(people: OsNode, segment: string): OsNode {
  let byId = personNodes.get(people);
  if (!byId) personNodes.set(people, (byId = new Map()));
  let node = byId.get(segment);
  if (!node) {
    const id = personIdOf(segment);
    node = {
      key: `${people.key}:${segment}`,
      path: `${people.path}/${segment}`,
      kind: "panel",
      name: id,
      symbol: symbolFor({ kind: "panel", name: "person" }),
      icon: "folder",
      summary: "what it keeps about them, and the way to forget them.",
      stats: [],
      panel: "agents",
    };
    byId.set(segment, node);
  }
  return node;
}

const PLACE_WORDS_OF: Record<AgentPlace, { name: string; summary: string }> = {
  overview: { name: "overview", summary: "what it is, what it is for, and what its memory did." },
  memory: { name: "memory", summary: "everything it keeps, newest first." },
  projects: { name: "projects", summary: "the projects tied to it: your own, opened here as in your memory." },
  "with-you": { name: "with you", summary: "what it learned about you, and where each piece stands." },
  activity: { name: "activity", summary: "what it did, as a ledger." },
  rituals: { name: "rituals", summary: "what it runs on a beat, where, and how the last runs went." },
  "sub-agents": { name: "sub-agents", summary: "the agents that work under it." },
  people: { name: "people", summary: "who it remembers, and what it keeps about each." },
  settings: { name: "settings", summary: "its picture, how it remembers, its keys and its webhook." },
};

/** the spaces an agent has, as nodes, in the strip's order: every space for a top-level agent, the same bar its
 *  sub-agents for a sub-agent, people only when it has them. Each carries the agent's own figure, so the stats line
 *  stays the agent's whichever space you stand in. */
function agentPlaceNodes(a: RootAgent, path: string, stats: OsStat[], top: boolean): OsNode[] {
  const out: OsNode[] = [];
  for (const place of AGENT_PLACES) {
    if (place === "sub-agents" && !top) continue;
    if (place === "people" && !hasPeople(a)) continue;
    const words = PLACE_WORDS_OF[place];
    const users = a.users ?? null;
    out.push({
      key: `agent-${place}:${a.id}`,
      path: `${path}/${place}`,
      kind: "panel",
      name: words.name,
      symbol: symbolFor({ kind: "panel", name: words.name }),
      icon: "folder",
      summary: words.summary,
      // a figure is never invented: a count that was not read is no figure
      stats: place === "people" ? (users == null ? [] : [{ label: users === 1 ? "person" : "people", value: groupNum(users) }]) : stats,
      lastActiveAt: place === "people" || place === "settings" ? null : (a.lastActiveAt ?? null),
      panel: "agents",
      ...(place === "projects" ? { agentProjects: { agentName: a.name, ties: a.projects ?? [] } } : {}),
    });
  }
  return out;
}

/** One agent → its node. `parent` is the node path it sits under (/agents, or
 *  /agents/<parent-slug> for a sub-agent); `subs` are its own sub-agents. Its
 *  children are its spaces (agentPlaceNodes), then its sub-agents, off the strip.
 *  Pure, so the server seed and the live splice agree. */
export function agentFolderNode(a: RootAgent, parent: string, subs: RootAgent[] = [], parentName?: string | null): OsNode {
  const path = `${parent.replace(/\/+$/, "")}/${agentSeg(a)}`;
  const kept = a.memories ?? null;
  const held = a.held ?? 0;
  const base = a.purpose?.trim() || (parentName ? `works under ${parentName}.` : "an agent you keep.");
  // the navigator shows the FIRST stat only: what it keeps leads, and a figure is
  // never invented when the count was not read (a seed row knows no memories yet)
  const stats: OsStat[] = [
    ...(kept == null ? [] : [{ label: kept === 1 ? "memory" : "memories", value: groupNum(kept) }]),
    ...(held > 0 ? [{ label: "waiting", value: held }] : []),
  ];
  return {
    key: `agent:${a.id}`,
    path,
    kind: "panel",
    name: a.name,
    symbol: symbolFor({ kind: "panel", name: a.name }),
    icon: "folder",
    summary: a.status === "paused" ? `paused. ${base}` : base,
    stats,
    lastActiveAt: a.lastActiveAt ?? null,
    panel: "agents",
    // its spaces, then its sub-agents, which stand off the strip (they are reached through its sub-agents space)
    children: [...agentPlaceNodes(a, path, stats, !parentName), ...subs.map((s) => ({ ...agentFolderNode(s, path, [], a.name), offStrip: true }))],
  };
}

const agentTime = (a: RootAgent) => (a.lastActiveAt ? Date.parse(a.lastActiveAt) || 0 : 0);

/** Every agent row → the children of the agents node: top-level agents, each
 *  carrying its sub-agents. Two levels only (the same rule the data keeps): a row
 *  whose parent is missing, or is itself a sub-agent, floats up to the top so it is
 *  never lost. Newest activity first, then by name, the way projects lead. */
export function buildAgentNodes(rows: RootAgent[]): OsNode[] {
  const bySlug = new Map(rows.map((r) => [r.slug, r]));
  const isTop = (r: RootAgent) => {
    if (!r.parentSlug) return true;
    const p = bySlug.get(r.parentSlug);
    return !p || !!p.parentSlug || p.slug === r.slug;
  };
  const order = (x: RootAgent, y: RootAgent) => agentTime(y) - agentTime(x) || x.name.localeCompare(y.name);
  const tops = rows.filter(isTop).sort(order);
  const subsOf = (slug: string) => rows.filter((r) => r.parentSlug === slug && !isTop(r)).sort(order);
  return tops.map((t) => agentFolderNode(t, "/agents", subsOf(t.slug)));
}

/** The agents panel node, with every agent hung under it (no children at all when
 *  there are none: an empty folder is a different thing from a panel with nothing yet). */
export function agentsSection(base: OsNode, rows: RootAgent[]): OsNode {
  const kids = buildAgentNodes(rows);
  const { children: _drop, ...rest } = base;
  const lastActiveAt = maxDate(...rows.map((r) => r.lastActiveAt)) ?? rest.lastActiveAt ?? null;
  return kids.length ? { ...rest, lastActiveAt, children: kids } : rest;
}

// the shapes a deep link into the agents space has: an agent (a slug), a sub-agent (a parent's slug and its own), and then
// optionally one of the spaces inside it (overview · memory · projects · … · settings · people · people/<user id>). A user id is what the agent was given for
// someone: letters, numbers and _ . : @ - (lib/agents/wire isExternalId), so it is the same set here, and never only dots.
const SLUG_PART = "[a-z0-9][a-z0-9-]{0,47}";
const PERSON_PART = "[A-Za-z0-9_.:@-]{1,128}";
const SPACE_PART = AGENT_PLACES.filter((p) => p !== "people").join("|");
const AGENT_PARAM = new RegExp(`^${SLUG_PART}(?:/${SLUG_PART})?(?:/(?:${SPACE_PART}|people(?:/${PERSON_PART})?))?$`);

/** The `?agent=` value, only when it is shaped like what the agents keep: one slug, or
 *  a parent's slug and a sub-agent's (a-z, 0-9 and hyphens, each starting with a letter
 *  or digit and at most 48 long, the way lib/agents/policy names them), and after
 *  either, one of the agent's places: `settings`, `people`, or `people/<user id>`.
 *  Anything else, a repeated parameter included, is no deep link at all and steers
 *  nothing. */
export function agentParamOf(v: string | string[] | undefined | null): string | undefined {
  return typeof v === "string" && AGENT_PARAM.test(v) && !/\/people\/\.+$/.test(v) ? v : undefined;
}

/** What a deep link names, as a path the shell can stand on. `?agent=grok-bot` and
 *  `?agent=grok-bot/deep-search` (and a lone sub-agent's slug, `?agent=deep-search`)
 *  resolve against the agents the person actually keeps: the sub-agent's page when
 *  it exists, else its parent's, else the agents home. After the agent, `settings`,
 *  `people` and `people/<user id>` name its places: its settings always, its people
 *  when it has them (a product agent, or one that keeps something about someone), and
 *  one person wherever the people place is (their id is not checked here: the page
 *  says so when it keeps nothing about them). Never a path the tree does not hold,
 *  so a stale or hand-typed link lands somewhere real. */
export function agentPathFor(param: string, rows: RootAgent[]): string {
  const parts = param.split("/").map((p) => p.trim()).filter(Boolean);
  const head = parts.map((p) => p.toLowerCase());
  const paths = new Set<string>();
  const agents: { path: string; slug: string }[] = [];
  const walk = (nodes: OsNode[]) => {
    for (const n of nodes) {
      paths.add(n.path);
      if (n.key.startsWith("agent:")) agents.push({ path: n.path, slug: n.path.split("/").pop() ?? "" });
      if (n.children) walk(n.children);
    }
  };
  walk(buildAgentNodes(rows));
  const isPlace = (x: string | undefined) => !!x && (AGENT_PLACES as readonly string[]).includes(x);

  // which agent, and how many of the segments named it
  let agent: string | null = null;
  let used = 0;
  if (head.length >= 2 && !isPlace(head[1]) && paths.has(`/agents/${head[0]}/${head[1]}`)) {
    agent = `/agents/${head[0]}/${head[1]}`;
    used = 2;
  } else if (head.length >= 1 && paths.has(`/agents/${head[0]}`)) {
    agent = `/agents/${head[0]}`;
    used = 1;
  } else if (head.length >= 1 && (head.length === 1 || isPlace(head[1]))) {
    // a sub-agent named alone
    agent = agents.find((p) => p.slug === head[0])?.path ?? null;
    used = 1;
  }
  if (!agent) return "/agents";

  const place = head[used];
  if (place === "people" && paths.has(`${agent}/people`)) {
    const who = parts[used + 1];
    return who && !/^\.+$/.test(who) ? agentPersonPath(agent, who) : `${agent}/people`;
  }
  if (isPlace(place) && paths.has(`${agent}/${place}`)) return `${agent}/${place}`;
  // an agent named alone lands on its overview, the way the shell lands on it
  return paths.has(`${agent}/overview`) ? `${agent}/overview` : agent;
}

/** The navigator's chip on the agents panel: every agent a person keeps, the
 *  sub-agents among them. One word for the one figure. */
export function agentsStat(rows: RootAgent[]): OsStat[] {
  return [{ label: rows.length === 1 ? "agent" : "agents", value: rows.length }];
}

export function buildRoot(
  projects: RootProject[],
  opts: { panelMeta?: PanelMeta; tasks?: boolean; ours?: boolean; agents?: boolean; agentRows?: RootAgent[]; spaces?: RootSpace[] } = {},
): OsNode {
  // Parked projects ride the SAME array flagged parked:true — routed OUT of the
  // /projects section and INTO the /parked node (with the parked branch ideas).
  const live = projects.filter((p) => !p.parked);
  const parkedProjects = projects.filter((p) => p.parked);
  // Only TOP-LEVEL projects sit at the root; nested ones appear inside their
  // parent (buildProjectSubtree). An orphan whose parent is missing/archived
  // floats back up so it's never lost.
  const ids = new Set(live.map((p) => p.id));
  const topLevel = live.filter((p) => !p.parentId || !ids.has(p.parentId));
  const projectFolders: OsNode[] = topLevel.map(projectFolderNode);

  const panelNode = (pa: { key: PanelKind; summary: string; hidden?: boolean }): OsNode => {
    const meta = opts.panelMeta?.[pa.key];
    return {
      key: pa.key,
      path: `/${pa.key}`,
      kind: "panel",
      name: pa.key,
      symbol: symbolFor({ kind: "panel", name: pa.key }),
      icon: "folder",
      summary: meta?.summary ?? pa.summary,
      stats: meta?.stats ?? [],
      lastActiveAt: meta?.lastActiveAt ?? null,
      panel: pa.key,
      hidden: pa.hidden,
    };
  };

  const projectsSection: OsNode = {
    key: "projects",
    path: "/projects",
    kind: "section",
    name: "projects",
    symbol: "⬡",
    icon: "folder",
    summary: "every project innernet holds, arranged around the organism.",
    stats: [{ label: live.length === 1 ? "project" : "projects", value: live.length }],
    lastActiveAt: maxDate(...live.map((p) => p.updated_at)),
    children: projectFolders,
  };

  // The park — a REAL directory whenever anything rests there, so the shell's
  // existing machinery renders it like any other level (ideas-only reads as the
  // artifacts view; parked projects sit as folders you can open). Empty, it stays
  // the panel with its resting copy. Hidden either way — reached by path.
  const parkedKids = buildParkedChildren(parkedProjects, opts.panelMeta?.parked?.parkedIdeas ?? []);
  const parkedNode = (): OsNode => {
    const base = panelNode(PANELS.find((p) => p.key === "parked")!);
    if (!parkedKids.length) return base;
    const { panel: _panel, ...rest } = base;
    return {
      ...rest,
      kind: "section",
      children: parkedKids,
      stats: opts.panelMeta?.parked?.stats ?? [{ label: "parked", value: parkedKids.length }],
      lastActiveAt: rest.lastActiveAt ?? maxDate(...parkedKids.map((c) => c.lastActiveAt)) ?? null,
    };
  };

  // Order matters — this IS the navigator strip: projects · you · netti ·
  // skills · connections · agents · activity · settings, then the hidden,
  // reachable-by-path panels (parked · dev). Projects comes FIRST because it is
  // where you arrive: it is the work.
  const byKey = (k: PanelKind) => PANELS.find((p) => p.key === k)!;
  const sections: OsNode[] = [
    projectsSection,
    panelNode(byKey("you")),
    panelNode(byKey("netti")),
    panelNode(byKey("skills")),
    panelNode(byKey("connections")),
    // the agents a person keeps — beta-gated (F.agents), so for everyone else the
    // strip reads exactly as it did before this space existed.
    // …and every agent hangs under it as a real path, so the shell can stand inside one
    // (an agent's page, with its sub-agents beneath it). The panel itself still
    // renders at /agents; kind stays "panel", the children are what the ≡ drills into.
    ...(opts.agents ? [agentsSection(panelNode(byKey("agents")), opts.agentRows ?? [])] : []),
    // shared spaces (ours) — founder-gated while the language settles. The panel
    // still RENDERS the rings (kind stays "panel"); the space children exist so
    // the ≡ menu drills into each room, which lives at its own route.
    ...(opts.ours ? [{ ...panelNode(byKey("ours")), children: (opts.spaces ?? []).map(spaceFolderNode) }] : []),
    panelNode(byKey("activity")),
    // the cross-project tasks space is beta-gated to specific accounts.
    ...(opts.tasks ? [panelNode(byKey("tasks"))] : []),
    panelNode(byKey("settings")),
    // Building on innernet lives in connections → api now (keys, the endpoint,
    // the whole REST surface) — there is no separate dev space to wake.
    ...PANELS.filter((pa) => pa.hidden).map((pa) => (pa.key === "parked" ? parkedNode() : panelNode(pa))),
  ];

  return {
    key: "root",
    path: "/",
    kind: "root",
    name: "innernet",
    symbol: "⊙",
    icon: "folder",
    summary: "",
    stats: [],
    children: sections,
  };
}

// ---- a project subtree ------------------------------------------------------

export interface ProjectDetail {
  id: string;
  slug: string;
  name: string;
  type: string;
  summary: string;
  /** `summary` is the project's own tagline, not the generated fallback. */
  hasTagline?: boolean;
  updatedAt: string;
  color?: string | null; // per-project folder colour override
  icon?: string | null; // per-project glyph/emoji override
  areas: { name: string; content: string; updatedAt: string }[];
  memories: { id: string; type: string; title: string; summary: string; body: string; updatedAt: string }[];
  artifacts: { id: string; title: string; summary: string | null; body: string; when: string; createdAt?: string | null }[];
  tasks: { id: string; title: string; status: string; description: string | null }[];
  commits: OsCommit[];
  subprojects?: RootProject[]; // child projects nested under this one
  /** a codebase (lib/os/codeProject.ts); the loader stamps it. */
  code?: boolean;
  /** code projects among `subprojects` (the loader counts; buildProjectSubtree
   *  falls back to counting the stamped subprojects itself). */
  codeChildren?: number;
  /** fresh captures resting here until netti folds them into the map. */
  inbox?: { hash: string; content: string; when: string }[];
  /** the handoff channel(s): the live document + the trackable trail of every
   *  previous handoff (migration 062 — history never destroyed again). */
  handoffs?: { scope: string; document: string; when: string; history: { document: string; when: string }[] }[];
}

export function buildProjectSubtree(d: ProjectDetail): OsNode {
  const base = `/projects/${d.id}`;
  const children: OsNode[] = [];

  // child projects nested under this one — folders you can open (recurses).
  for (const sp of d.subprojects ?? []) children.push(projectFolderNode(sp));
  const codeKids = d.codeChildren ?? (d.subprojects ?? []).filter((sp) => sp.code).length;

  // dimensions → document pages. A dimension whose frontmatter says
  // `kind: skill` is a SKILL (law_skill_class) — same document, wearing the
  // modifier — and gathers into the project's `skills` folder below instead of
  // lying flat among the domains. Searching "skills" finds the folder; each
  // skill is searchable by its own title.
  const skillPages: OsNode[] = [];
  for (const a of d.areas) {
    const skill = parseSkillDoc(a.content, seg(a.name));
    if (skill) {
      skillPages.push({
        key: `dim:${seg(a.name)}`,
        path: `${base}/skills/${seg(a.name)}`,
        kind: "dimension",
        name: skill.manifest.title,
        symbol: "✦",
        icon: "page",
        summary: skill.manifest.description,
        stats: [{ label: "skill", value: "" }],
        lastActiveAt: a.updatedAt,
        document: a.content,
        projectId: d.id,
        editKey: a.name, // the raw dimension name — the save endpoint targets this row
      });
      continue;
    }
    children.push({
      key: `dim:${seg(a.name)}`,
      path: `${base}/${seg(a.name)}`,
      kind: "dimension",
      name: a.name.replace(/[_-]/g, " "),
      symbol: symbolFor({ kind: "dimension", name: a.name }),
      icon: "page",
      summary: firstLine(a.content) || "a domain of this project.",
      stats: [{ label: "domain", value: "" }],
      lastActiveAt: a.updatedAt,
      document: a.content,
      projectId: d.id,
      editKey: a.name, // the raw dimension name — the save endpoint targets this row
    });
  }

  // skills → their own folder: what this project can teach an AI, each page a
  // shareable link (share mints just that page — law_share_is_a_moment).
  if (skillPages.length) {
    children.push({
      key: "grp:skills",
      path: `${base}/skills`,
      kind: "group",
      name: "skills",
      symbol: "✦",
      icon: "folder",
      summary: "things an ai can learn from this project — each one a link you can hand out.",
      stats: [{ label: skillPages.length === 1 ? "skill" : "skills", value: skillPages.length }],
      lastActiveAt: maxDate(...skillPages.map((s) => s.lastActiveAt ?? "")),
      projectId: d.id,
      children: skillPages,
    });
  }

  // nodes → grouped by type into folders, each holding node pages.
  // EXCEPT the artifact-typed ones: "artifact" is not a species of node, it is
  // what every document already is (see OsNode.live). A node typed `artifact`
  // used to mint its own `artifacts` folder at exactly the path the project's
  // living documents claim — same key, same path — and the collision shadowed
  // every real document in the project. They are the same folder because they
  // are the same thing, so they gather there instead.
  const byType = new Map<string, ProjectDetail["memories"]>();
  const artifactMems: ProjectDetail["memories"] = [];
  for (const m of d.memories) {
    if (seg(plural(m.type)) === "artifacts") {
      artifactMems.push(m);
      continue;
    }
    const arr = byType.get(m.type) ?? [];
    arr.push(m);
    byType.set(m.type, arr);
  }
  for (const [type, mems] of byType) {
    children.push({
      key: `grp:${seg(type)}`,
      path: `${base}/${seg(plural(type))}`,
      kind: "group",
      name: plural(type),
      symbol: symbolFor({ kind: "group", name: type, type }),
      icon: "folder",
      summary: `${mems.length} ${mems.length === 1 ? "thing" : "things"} innernet holds as ${plural(type)}.`,
      stats: [{ label: mems.length === 1 ? "item" : "items", value: mems.length }],
      lastActiveAt: maxDate(...mems.map((m) => m.updatedAt)),
      projectId: d.id,
      children: mems.map((m) => ({
        key: `node:${m.id}`,
        path: `${base}/${seg(plural(type))}/${seg(m.title)}`,
        kind: "node" as const,
        name: m.title,
        symbol: symbolFor({ kind: "node", name: m.title, type: m.type }),
        icon: "page" as const,
        summary: m.summary,
        stats: [{ label: type, value: "" }],
        lastActiveAt: m.updatedAt,
        document: m.body || `**${m.title}**\n\n${m.summary}`,
        projectId: d.id,
      })),
    });
  }

  // artifacts → ONE folder of this project's documents. Two sources feed it and
  // they are not two kinds of thing: the documents innernet keeps re-organizing
  // as fragments stream in (`live`), and the artifact-typed memory nodes that
  // used to collide with them. Each page keeps its own key prefix, which is
  // also its single source of truth — `art:` reads and writes the artifacts
  // table, `node:` the nodes table — so rename · edit · autoname · paste all
  // keep routing to the right row without knowing they are shelved together.
  const artifactPages: OsNode[] = [
    ...d.artifacts.map((a) => ({
      key: `art:${a.id}`,
      path: `${base}/artifacts/${seg(a.title)}`,
      kind: "artifact" as const,
      name: a.title,
      symbol: symbolFor({ kind: "artifact", name: a.title }),
      icon: "page" as const,
      summary: a.summary || firstLine(a.body) || "a living document.",
      stats: [{ label: "document", value: "" }],
      lastActiveAt: a.when,
      createdAt: a.createdAt ?? null,
      document: a.body,
      projectId: d.id,
      live: true, // innernet re-organizes this one as findings arrive
    })),
    ...artifactMems.map((m) => ({
      key: `node:${m.id}`,
      path: `${base}/artifacts/${seg(m.title)}`,
      kind: "artifact" as const,
      name: m.title,
      symbol: symbolFor({ kind: "artifact", name: m.title }),
      icon: "page" as const,
      summary: m.summary,
      stats: [{ label: "document", value: "" }],
      lastActiveAt: m.updatedAt,
      document: m.body || `**${m.title}**\n\n${m.summary}`,
      projectId: d.id,
      live: false, // a document all the same — just not running yet
    })),
  ];
  if (artifactPages.length) {
    // two documents may still want the same slug (a living one and a node that
    // share a title). Paths are how the shell finds a page, so make them unique
    // — first claim keeps the clean slug, later ones take a suffix.
    const claimed = new Set<string>();
    for (const p of artifactPages) {
      if (!claimed.has(p.path)) {
        claimed.add(p.path);
        continue;
      }
      let n = 2;
      while (claimed.has(`${p.path}-${n}`)) n++;
      p.path = `${p.path}-${n}`;
      claimed.add(p.path);
    }
    artifactPages.sort(
      (a, b) => new Date(b.lastActiveAt ?? 0).getTime() - new Date(a.lastActiveAt ?? 0).getTime(),
    );
    const liveCount = artifactPages.filter((p) => p.live).length;
    children.push({
      key: "grp:artifacts",
      path: `${base}/artifacts`,
      kind: "group",
      name: "artifacts",
      symbol: "❋",
      icon: "folder",
      summary: liveCount
        ? "documents this project holds — innernet keeps re-organizing the live ones as findings stream in."
        : "documents this project holds — any of them can be set running.",
      stats: [{ label: artifactPages.length === 1 ? "document" : "documents", value: artifactPages.length }],
      lastActiveAt: maxDate(...artifactPages.map((p) => p.lastActiveAt ?? "")),
      projectId: d.id,
      children: artifactPages,
    });
  }

  // fresh captures → the INBOX: landed but not yet folded. Every capture is an
  // openable page from the moment it arrives; when netti folds, these pages
  // dissolve and the change reappears inside the dimensions/nodes it enriched.
  // No projectId on the pages — a raw capture is read-only (rename/move stay off).
  if (d.inbox?.length) {
    children.push({
      key: "grp:inbox",
      path: `${base}/inbox`,
      kind: "group",
      name: "inbox",
      symbol: "◌",
      icon: "folder",
      summary: "fresh captures resting here until netti folds them into the map.",
      stats: [{ label: d.inbox.length === 1 ? "fresh capture" : "fresh captures", value: d.inbox.length }],
      lastActiveAt: maxDate(...d.inbox.map((c) => c.when)),
      projectId: d.id,
      children: d.inbox.map((c) => {
        const line = firstLine(c.content) || "a fresh capture";
        return {
          key: `cap:${c.hash}`,
          path: `${base}/inbox/${c.hash}`,
          kind: "node" as const,
          name: line.length > 48 ? `${line.slice(0, 48).trimEnd()}…` : line,
          symbol: "◌",
          icon: "page" as const,
          summary: "waiting for netti to fold this into the map.",
          stats: [{ label: "fresh", value: "" }],
          lastActiveAt: c.when,
          createdAt: c.when,
          document: `${c.content}\n\n*captured ${fmtDate(c.when)} · waiting for netti to fold it into the map*`,
        };
      }),
    });
  }

  // the handoff channel → a folder holding the LIVE "where I left off" document
  // plus the trackable trail of every handoff before it (nothing overwritten).
  if (d.handoffs?.length) {
    const pages: OsNode[] = [];
    for (const h of d.handoffs) {
      const scopeSeg = seg(h.scope) || "project";
      const label = h.scope === "project" ? "" : `${h.scope} · `;
      pages.push({
        key: `ho:${scopeSeg}`,
        path: `${base}/handoffs/${scopeSeg}`,
        kind: "artifact",
        name: `${label}latest`,
        symbol: "⤴",
        icon: "page",
        summary: firstLine(h.document) || "the live where-I-left-off stream.",
        stats: [{ label: "live", value: "" }],
        lastActiveAt: h.when,
        document: h.document,
      });
      h.history.forEach((e, i) => {
        const t = new Date(e.when);
        // am/pm, never military time — see lib/format/time.ts
        const hm = Number.isNaN(t.getTime()) ? "" : ` ${fmtClock(t)}`;
        pages.push({
          key: `hoe:${scopeSeg}:${i}`,
          path: `${base}/handoffs/${scopeSeg}-${i + 1}`,
          kind: "artifact",
          name: `${label}${fmtDate(e.when)}${hm}`,
          symbol: "⤴",
          icon: "page",
          summary: firstLine(e.document) || "an earlier handoff.",
          stats: [{ label: "handoff", value: "" }],
          lastActiveAt: e.when,
          createdAt: e.when,
          document: e.document,
        });
      });
    }
    children.push({
      key: "grp:handoffs",
      path: `${base}/handoffs`,
      kind: "group",
      name: "handoffs",
      symbol: "⤴",
      icon: "folder",
      summary: "the where-I-left-off stream — the live handoff, and every one before it.",
      stats: [{ label: pages.length === 1 ? "entry" : "entries", value: pages.length }],
      lastActiveAt: maxDate(...pages.map((p) => p.lastActiveAt)),
      projectId: d.id,
      children: pages,
    });
  }

  // tasks → a folder of open loops
  if (d.tasks.length) {
    children.push({
      key: "grp:tasks",
      path: `${base}/tasks`,
      kind: "group",
      name: "tasks",
      symbol: "⌖",
      icon: "folder",
      summary: "open loops in motion on this project.",
      stats: [{ label: d.tasks.length === 1 ? "task" : "tasks", value: d.tasks.length }],
      projectId: d.id,
      children: d.tasks.map((t) => ({
        key: `task:${t.id}`,
        path: `${base}/tasks/${seg(t.title)}`,
        kind: "task" as const,
        name: t.title,
        symbol: "⌖",
        icon: "page" as const,
        summary: t.description || `a task that is ${t.status.replace(/_/g, " ")}.`,
        stats: [{ label: t.status.replace(/_/g, " "), value: "" }],
        document: `# ${t.title}\n\n*status — ${t.status.replace(/_/g, " ")}*\n\n${t.description ?? ""}`,
        projectId: d.id,
      })),
    });
  }

  return {
    key: d.id,
    path: base,
    kind: "project",
    name: d.name,
    // honor the hand-picked icon + colour here too — this node REPLACES the
    // root-level one when the subtree loads, and used to silently drop both.
    symbol: symbolFor({ kind: "project", name: d.name, type: d.type }, d.icon),
    icon: "folder",
    color: d.color ?? null,
    iconKey: d.icon ?? null,
    summary: d.summary,
    stats: [{ label: "cells", value: d.areas.length + d.memories.length }],
    lastActiveAt: d.updatedAt,
    projectId: d.id,
    children,
    commits: d.commits,
    projectType: d.type,
    ...(d.hasTagline ? { hasTagline: true } : {}),
    ...(d.code ? { code: true } : {}),
    ...(codeKids ? { codeChildren: codeKids } : {}),
  };
}
